/* global console */

import type { FetchAgreementsReportResult } from '../../../agreements/report/fetch-agreements-report';
import type { ReportPipelineResult } from '../../../shared/report-pipeline-result.model';
import type { TabularSheetPayload } from '../../../shared/tabular-sheet-payload.model';
import type { TabularSheetWriteMode } from '../../../shared/tabular-sheet-write-mode.model';

/**
 * After a feature fetch succeeds, builds tabular payload and writes to the sheet; maps all outcomes to
 * {@link ReportPipelineResult}.
 */
export async function completeFetchedReportWithTabularWrite(
    fetchResult: FetchAgreementsReportResult,
    toTabular: (records: unknown[]) => TabularSheetPayload,
    writeTabular: (
        payload: TabularSheetPayload,
        mode: TabularSheetWriteMode
    ) => Promise<{ sheetName: string; sheetId: string; tableCreated: boolean }>,
    tabularWriteMode: TabularSheetWriteMode
): Promise<ReportPipelineResult> {
    if (fetchResult.kind === 'failure') {
        return {
            kind: 'failure',
            message: fetchResult.message,
            source: fetchResult.source,
            ...(fetchResult.invalidElementIds !== undefined
                ? { invalidElementIds: fetchResult.invalidElementIds }
                : {}),
            ...(fetchResult.httpStatus !== undefined ? { httpStatus: fetchResult.httpStatus } : {}),
            ...(fetchResult.body !== undefined ? { body: fetchResult.body } : {}),
            debugErrorTitle: fetchResult.debugErrorTitle,
        };
    }

    const { body, records, totalCount, recordCount, httpStatus } = fetchResult;

    try {
        const tabular = toTabular(records);
        const { sheetName, sheetId, tableCreated } = await writeTabular(tabular, tabularWriteMode);
        return {
            kind: 'success',
            recordCount,
            totalCount,
            sheetName,
            sheetId,
            tableCreated,
            responseBody: body,
        };
    } catch (worksheetError) {
        console.error('Excel table write failed', worksheetError);
        return {
            kind: 'failure',
            message: `Data fetched (${recordCount} record(s)) but writing to Excel failed: ${worksheetError instanceof Error ? worksheetError.message : String(worksheetError)}`,
            source: 'worksheet',
            httpStatus,
            body,
            debugErrorTitle: 'Data fetched but writing to Excel failed',
        };
    }
}
